"use client";

import { useTranslations } from "next-intl";
import { ArrowLeft, Heart, ExternalLink, BookOpen, Lightbulb, HelpCircle, Check } from "lucide-react";
import { Link } from "@/i18n/navigation";

export function AboutClient() {
  const t = useTranslations();
  const whyList = (t.raw("about_why_list") || []) as string[];

  return (
    <div className="w-full max-w-3xl mx-auto px-4 py-8" data-testid="about-page">
      <Link
        href="/"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-primary transition-colors mb-8"
        data-testid="about-back"
      >
        <ArrowLeft className="w-4 h-4" />
        {t("btn_back")}
      </Link>

      <div className="bg-card border border-border rounded-3xl p-8 md:p-10 card-elevated">
        <div className="flex items-center gap-4 mb-8">
          <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Heart className="w-8 h-8 text-primary" />
          </div>
          <div>
            <h1 className="font-display text-3xl md:text-4xl font-semibold text-foreground">
              {t("about_title")}
            </h1>
            <p className="text-muted-foreground mt-1">{t("footer_sub")}</p>
          </div>
        </div>

        <div className="space-y-8">
          <section>
            <p className="text-foreground/90 leading-relaxed text-lg">
              {t("about_intro")}
            </p>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-4">
              {t("about_concept_title")}
            </h2>
            <p className="text-foreground/80 leading-relaxed">
              {t("about_concept_text")}
            </p>
          </section>

          <section className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="p-2.5 rounded-xl bg-amber-100 dark:bg-amber-900/30 flex-shrink-0">
                <BookOpen className="w-5 h-5 text-amber-600 dark:text-amber-400" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold text-foreground mb-3">
                  {t("about_history_title")}
                </h2>
                <p className="text-foreground/80 leading-relaxed">
                  {t("about_history_text")}
                </p>
              </div>
            </div>
          </section>

          <section className="bg-emerald-50 dark:bg-emerald-950/20 border border-emerald-200 dark:border-emerald-800 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="p-2.5 rounded-xl bg-emerald-100 dark:bg-emerald-900/30 flex-shrink-0">
                <Lightbulb className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold text-foreground mb-4">
                  {t("about_why_title")}
                </h2>
                <ul className="space-y-3">
                  {whyList.map((item, i) => (
                    <li key={i} className="flex gap-3 text-foreground/80">
                      <Check className="w-5 h-5 text-emerald-500 flex-shrink-0 mt-0.5" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </section>

          <section className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-2xl p-6">
            <div className="flex items-start gap-4">
              <div className="p-2.5 rounded-xl bg-blue-100 dark:bg-blue-900/30 flex-shrink-0">
                <HelpCircle className="w-5 h-5 text-blue-600 dark:text-blue-400" />
              </div>
              <div>
                <h2 className="font-display text-xl font-semibold text-foreground mb-3">
                  {t("about_quiz_title")}
                </h2>
                <p className="text-foreground/80 leading-relaxed">
                  {t("about_quiz_text")}
                </p>
              </div>
            </div>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-4">
              {t("about_creator_title")}
            </h2>
            <p className="text-foreground/80 leading-relaxed mb-4">
              {t("about_creator_text")}
            </p>
            <a
              href="https://tr.ee/jow"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-5 py-3 rounded-xl font-medium hover:opacity-90 transition-opacity"
              data-testid="about-social-link"
            >
              <ExternalLink className="w-4 h-4" />
              {t("about_social_link")}
            </a>
          </section>

          <section>
            <h2 className="font-display text-xl font-semibold text-foreground mb-4">
              {t("about_credits_title")}
            </h2>
            <ul className="space-y-3 text-foreground/80">
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                {t("about_credits_book")}
              </li>
              <li className="flex items-start gap-3">
                <span className="text-primary">•</span>
                {t("about_credits_bible")}
              </li>
            </ul>
          </section>

          <section className="bg-secondary/50 rounded-2xl p-6 border border-border">
            <p className="text-sm text-muted-foreground leading-relaxed">
              {t("about_disclaimer")}
            </p>
          </section>
        </div>
      </div>
    </div>
  );
}
